import { useState, useEffect, useMemo } from "react";
import axios from "../utils/axios";
import {
  formatChannelOptionLabel,
  sortChannelsByCategoryAndName,
} from "../utils/channelOptions";
import Spinner from "./Spinner";
import CollapsibleSection from "./CollapsibleSection";
import "./GuildModules.css";

interface Channel {
  id: string;
  name: string;
  type?: number;
  parentId?: string | null;
  categoryName?: string | null;
}

interface GuildModule {
  id: number;
  guild_id: string;
  module_id: string;
  enabled: boolean;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface AvailableModule {
  id: string;
  name: string;
  description: string;
  version: string;
  defaultConfig: Record<string, any>;
  configSchema: Record<string, ConfigField>;
}

type ConfigFieldType = "string" | "number" | "boolean" | "array" | "password";

interface ConfigField {
  type: ConfigFieldType;
  label: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  scope?: "guild" | "global";
  inputType?: "textarea";
}

interface ModuleSnapshot {
  module_id: string;
  enabled: boolean;
}

interface GuildModulesProps {
  guildId: string;
  channels: Channel[];
  onModulesLoaded?: (modules: ModuleSnapshot[]) => void;
}

const SECRET_PLACEHOLDER = "__ENCRYPTED__";
const GLOBAL_ONLY_FIELDS: Record<string, string[]> = {
  live: ["pollIntervalSeconds"],
};
const isGuildScopedField = (
  moduleId: string,
  fieldName: string,
  field?: ConfigField,
): boolean => {
  if (!field) {
    return true;
  }
  if (field.scope === "global") {
    return false;
  }
  const fallback = GLOBAL_ONLY_FIELDS[moduleId];
  if (fallback?.includes(fieldName)) {
    return false;
  }
  return true;
};

function GuildModules({ guildId, channels, onModulesLoaded }: GuildModulesProps) {
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>(
    [],
  );
  const [guildModules, setGuildModules] = useState<GuildModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingModule, setEditingModule] = useState<string | null>(null);
  const [editConfigs, setEditConfigs] = useState<
    Record<string, Record<string, any>>
  >({});
  const [collapsedModules, setCollapsedModules] = useState<
    Record<string, boolean>
  >({});
  const [testingModuleId, setTestingModuleId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<
      string,
      { status: "success" | "error"; message: string; link?: string }
    >
  >({});

  const sortedChannels = sortChannelsByCategoryAndName(channels);

  useEffect(() => {
    fetchModules();
  }, [guildId]);

  const visibleModuleIds = useMemo(
    () => new Set(availableModules.map((module) => module.id)),
    [availableModules],
  );
  const enabledModulesCount = useMemo(
    () =>
      guildModules.filter(
        (guildModule) =>
          guildModule.enabled && visibleModuleIds.has(guildModule.module_id),
      ).length,
    [guildModules, visibleModuleIds],
  );

  const fetchModules = async () => {
    setLoading(true);
    try {
      const [availableRes, guildRes] = await Promise.all([
        axios.get("/api/modules/available", { params: { guildId } }),
        axios.get(`/api/server/${guildId}/modules`),
      ]);

      console.log("Available modules response:", availableRes.data);
      console.log("Guild modules response:", guildRes.data);

      const available = availableRes.data.modules || [];
      const guildData = guildRes.data.modules || [];

      setAvailableModules(available);
      setGuildModules(guildData);

      if (onModulesLoaded) {
        const snapshot = guildData.map((module: GuildModule) => ({
          module_id: module.module_id,
          enabled: Boolean(module.enabled),
        }));
        onModulesLoaded(snapshot);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      setAvailableModules([]);
      setGuildModules([]);
      if (onModulesLoaded) {
        onModulesLoaded([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getGuildModuleForModuleId = (
    moduleId: string,
  ): GuildModule | undefined => {
    return guildModules.find((m) => m.module_id === moduleId);
  };

  const toggleModuleCollapse = (moduleId: string) => {
    setCollapsedModules((prev) => {
      const current = prev[moduleId] ?? true; // default collapsed until toggled
      return {
        ...prev,
        [moduleId]: !current,
      };
    });
  };

  const handleToggleModule = async (
    module: AvailableModule,
    currentlyEnabled: boolean,
  ) => {
    const guildModule = getGuildModuleForModuleId(module.id);
    setSaving(module.id);

    try {
      if (guildModule) {
        // Update existing module
        await axios.put(`/api/server/${guildId}/modules/${guildModule.id}`, {
          enabled: currentlyEnabled ? 0 : 1,
        });
      } else {
        // Add new module
        await axios.post(`/api/server/${guildId}/modules`, {
          moduleId: module.id,
          enabled: 1,
          config: module.defaultConfig,
        });
      }

      await fetchModules();
    } catch (error) {
      console.error("Error toggling module:", error);
      alert("Failed to toggle module");
    } finally {
      setSaving(null);
    }
  };

  const handleEditConfig = (moduleId: string) => {
    const guildModule = getGuildModuleForModuleId(moduleId);
    if (guildModule) {
      const moduleDef = availableModules.find((mod) => mod.id === moduleId);
      const nextConfig = { ...guildModule.config };

      if (moduleDef) {
        Object.entries(moduleDef.configSchema)
          .filter(([fieldName, field]) =>
            isGuildScopedField(moduleId, fieldName, field),
          )
          .forEach(([fieldName, field]) => {
            if (field.type === "password") {
              delete nextConfig[fieldName];
            }
          });
      }

      setEditConfigs({
        ...editConfigs,
        [moduleId]: nextConfig,
      });
      setEditingModule(moduleId);
    }
  };

  const handleCancelEdit = (moduleId: string) => {
    const newConfigs = { ...editConfigs };
    delete newConfigs[moduleId];
    setEditConfigs(newConfigs);
    setEditingModule(null);
  };

  const handleConfigChange = (moduleId: string, field: string, value: any) => {
    setEditConfigs({
      ...editConfigs,
      [moduleId]: {
        ...(editConfigs[moduleId] || {}),
        [field]: value,
      },
    });
  };

  const handleSaveConfig = async (module: AvailableModule) => {
    const guildModule = getGuildModuleForModuleId(module.id);
    if (!guildModule) return;

    setSaving(module.id);

    try {
      await axios.put(`/api/server/${guildId}/modules/${guildModule.id}`, {
        config: editConfigs[module.id] || guildModule.config,
      });

      await fetchModules();
      setEditingModule(null);

      const newConfigs = { ...editConfigs };
      delete newConfigs[module.id];
      setEditConfigs(newConfigs);
    } catch (error) {
      console.error("Error saving module config:", error);
      alert("Failed to save module configuration");
    } finally {
      setSaving(null);
    }
  };

  const handleSendGreetingTest = async (module: AvailableModule) => {
    if (module.id !== "greetings") return;

    const guildModule = getGuildModuleForModuleId(module.id);
    const config = guildModule?.config || module.defaultConfig || {};

    if (!config.channelId) {
      setTestResults((prev) => ({
        ...prev,
        [module.id]: {
          status: "error",
          message: "Set a greeting channel before sending a test.",
        },
      }));
      return;
    }

    setTestingModuleId(module.id);
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[module.id];
      return next;
    });

    try {
      const response = await axios.post(
        `/api/server/${guildId}/modules/${module.id}/test`,
      );

      const channelId = response.data?.channelId as string | undefined;
      const targetChannel = channelId
        ? channels.find((c) => c.id === channelId)
        : null;
      const channelLabel = targetChannel
        ? formatChannelOptionLabel(targetChannel)
        : channelId
          ? `#${channelId}`
          : "the configured channel";

      setTestResults((prev) => ({
        ...prev,
        [module.id]: {
          status: "success",
          message: `Sent a greeting preview to ${channelLabel}.`,
          link: response.data?.messageUrl as string | undefined,
        },
      }));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        "Failed to send a greeting preview. Please verify the configuration.";

      setTestResults((prev) => ({
        ...prev,
        [module.id]: { status: "error", message: errorMessage },
      }));
    } finally {
      setTestingModuleId(null);
    }
  };

  const renderConfigField = (
    module: AvailableModule,
    fieldName: string,
    field: ConfigField,
    currentValue: any,
    isEditing: boolean,
    inputId: string,
  ) => {
    const ariaLabel = field.label || fieldName;

    if (!isEditing) {
      const isMultiline =
        field.inputType === "textarea" ||
        (typeof currentValue === "string" && currentValue.includes("\n"));

      // Display mode
      if (fieldName === "channelId" && currentValue) {
        const channel = channels.find((c) => c.id === currentValue);
        return (
          <span
            className={`config-value ${
              isMultiline ? "config-value--multiline" : ""
            }`}
          >
            {channel ? formatChannelOptionLabel(channel) : currentValue}
          </span>
        );
      }
      if (field.type === "password") {
        if (currentValue === SECRET_PLACEHOLDER) {
          return <span className="config-value">••••••••</span>;
        }
        return (
          <span className="config-value">
            <em className="config-empty">(not set)</em>
          </span>
        );
      }
      if (field.type === "array") {
        return (
          <span
            className={`config-value ${
              isMultiline ? "config-value--multiline" : ""
            }`}
          >
            {JSON.stringify(currentValue || [])}
          </span>
        );
      }
      return (
        <span
          className={`config-value ${
            isMultiline ? "config-value--multiline" : ""
          }`}
        >
          {currentValue || <em className="config-empty">(not set)</em>}
        </span>
      );
    }

    // Edit mode
    const editValue = editConfigs[module.id]?.[fieldName] ?? currentValue;

    if (fieldName === "channelId") {
      return (
        <select
          className="config-input"
          id={inputId}
          name={fieldName}
          value={editValue || ""}
          onChange={(e) =>
            handleConfigChange(module.id, fieldName, e.target.value)
          }
        >
          <option value="">All channels</option>
          {sortedChannels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {formatChannelOptionLabel(channel)}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "array") {
      return (
        <textarea
          className="config-input config-textarea"
          id={inputId}
          name={fieldName}
          aria-label={ariaLabel}
          value={JSON.stringify(editValue || [], null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              handleConfigChange(module.id, fieldName, parsed);
            } catch {
              // Invalid JSON, don't update
            }
          }}
          placeholder={field.placeholder || "[]"}
        />
      );
    }

    if (field.inputType === "textarea") {
      return (
        <textarea
          className="config-input config-textarea"
          id={inputId}
          name={fieldName}
          aria-label={ariaLabel}
          value={editValue || ""}
          onChange={(e) =>
            handleConfigChange(module.id, fieldName, e.target.value)
          }
          placeholder={field.placeholder || ""}
        />
      );
    }

    if (field.type === "password") {
      const editValue = editConfigs[module.id]?.[fieldName] ?? "";
      return (
        <input
          type="password"
          className="config-input"
          id={inputId}
          name={fieldName}
          value={editValue}
          onChange={(e) =>
            handleConfigChange(module.id, fieldName, e.target.value)
          }
          placeholder={field.placeholder || "Enter new password"}
        />
      );
    }

    return (
      <input
        type="text"
        className="config-input"
        id={inputId}
        name={fieldName}
        value={editValue || ""}
        onChange={(e) =>
          handleConfigChange(module.id, fieldName, e.target.value)
        }
        placeholder={field.placeholder || ""}
      />
    );
  };

  if (loading) {
    return (
      <div className="guild-modules-section">
        <div className="loading-container">
          <Spinner />
          <p>Loading modules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="guild-modules-section">
      <CollapsibleSection
        title="🔧 Guild Modules"
        count={enabledModulesCount}
        defaultCollapsed={true}
      >
        <p className="modules-description">
          Enable and configure command modules for this server. Modules add new
          bot functionality that can be customized per server.
        </p>

        {availableModules.length === 0 ? (
          <div className="no-modules">
            <p>No modules available</p>
          </div>
        ) : (
          <div className="modules-list">
            {availableModules.map((module) => {
              const guildModule = getGuildModuleForModuleId(module.id);
              const isEnabled = guildModule?.enabled || false;
              const isSaving = saving === module.id;
              const isEditing = editingModule === module.id;
              const currentConfig = guildModule?.config || module.defaultConfig;
              const isCollapsed = collapsedModules[module.id] ?? true; // default collapsed until toggled
              const schemaEntries = Object.entries(module.configSchema || {});
              const configFields = schemaEntries.filter(
                ([fieldName, field]) =>
                  isGuildScopedField(module.id, fieldName, field),
              );
              const hasGlobalSettings = schemaEntries.some(
                ([, field]) => field?.scope === "global",
              );
              const onlyGlobalSettings =
                hasGlobalSettings && configFields.length === 0;

              return (
                <div key={module.id} className="module-card">
                  <div
                    className="module-header"
                    onClick={() => toggleModuleCollapse(module.id)}
                  >
                    <span
                      className={`collapse-icon ${isCollapsed ? "collapsed" : ""}`}
                    >
                      ▼
                    </span>
                    <div className="module-info">
                      <span className="module-name">{module.name}</span>
                      <span className="module-version">v{module.version}</span>
                      {isEnabled && (
                        <span className="module-status-enabled">Enabled</span>
                      )}
                      {!isEnabled && (
                        <span className="module-status-disabled">Disabled</span>
                      )}
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="module-content">
                      <p className="module-description">{module.description}</p>

                      <div className="module-toggle">
                        <button
                          className={`btn ${isEnabled ? "btn-danger" : "btn-primary"}`}
                          onClick={() => handleToggleModule(module, isEnabled)}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <Spinner size="small" />
                          ) : isEnabled ? (
                            "Disable Module"
                          ) : (
                            "Enable Module"
                          )}
                        </button>
                      </div>

                      {isEnabled && guildModule && configFields.length > 0 && (
                        <div className="module-config">
                          <h4>Configuration</h4>

                          <div className="config-fields">
                            {configFields.map(([fieldName, field]) => {
                              const inputId = `${module.id}-${fieldName}`;

                              return (
                                <div key={fieldName} className="config-field">
                                  <label className="config-label" htmlFor={inputId}>
                                    {field.label}
                                    {field.required && (
                                      <span className="required">*</span>
                                    )}
                                  </label>

                                  {renderConfigField(
                                    module,
                                    fieldName,
                                    field,
                                    currentConfig[fieldName],
                                    isEditing,
                                    inputId,
                                  )}

                                  {field.description && (
                                    <span className="config-description">
                                      {field.description}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <div className="config-actions">
                            {!isEditing ? (
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleEditConfig(module.id)}
                              >
                                Edit Configuration
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleSaveConfig(module)}
                                  disabled={isSaving}
                                >
                                  {isSaving ? (
                                    <Spinner size="small" />
                                  ) : (
                                    "Save Changes"
                                  )}
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleCancelEdit(module.id)}
                                  disabled={isSaving}
                                >
                                  Cancel
                                </button>
                              </>
                            )}

                            {module.id === "greetings" && (
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleSendGreetingTest(module)}
                                disabled={
                                  isSaving ||
                                  isEditing ||
                                  testingModuleId === module.id
                                }
                              >
                                {testingModuleId === module.id ? (
                                  <Spinner size="small" />
                                ) : (
                                  "Send Test Greeting"
                                )}
                              </button>
                            )}
                          </div>

                          {module.id === "greetings" &&
                            testResults[module.id] && (
                              <div
                                className={`module-test-feedback ${
                                  testResults[module.id].status
                                }`}
                              >
                                <div>{testResults[module.id].message}</div>
                                {testResults[module.id].link && (
                                  <div>
                                    <a
                                      href={testResults[module.id].link}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      View test message
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                      )}

                      {isEnabled && onlyGlobalSettings && (
                        <div className="module-global-note">
                          Twitch polling settings for this module are managed
                          globally from the Admin Panel to prevent
                          server-wide performance issues.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}

export default GuildModules;
